<template>
    <div class="ai-draft-card ai-draft-resume">
        <h2>Pick up where you left off</h2>
        <p class="ai-draft-muted">{{ sessions.length }} draft<span v-if="sessions.length !== 1">s</span> in progress</p>

        <ul class="ai-draft-resume-list">
            <li
                v-for="s in sessions"
                :key="s.id"
                class="ai-draft-resume-item"
                @click="$emit('resume', s)"
            >
                <div class="ai-draft-resume-icon">
                    <i class="fa fa-comment-dots"></i>
                </div>
                <div class="ai-draft-resume-body">
                    <div class="ai-draft-resume-title">
                        <span v-if="s.quote_id">Quote #{{ s.quote_id }}</span>
                        <span v-else>Untitled draft</span>
                        <span class="ai-draft-resume-tokens">{{ formatTokens(s) }}</span>
                    </div>
                    <div class="ai-draft-resume-meta">
                        Started {{ relativeTime(s.created_at) }} ·
                        last activity {{ relativeTime(s.updated_at) }}
                    </div>
                </div>
                <button
                    type="button"
                    class="ai-draft-resume-discard"
                    title="Discard this draft"
                    @click.stop="discard(s)"
                >
                    <i class="fa fa-times"></i>
                </button>
                <div class="ai-draft-resume-arrow">
                    <i class="fa fa-arrow-right"></i>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
import api from '../../api';

export default {
    props: {
        sessions: { type: Array, required: true },
    },

    methods: {
        discard(session) {
            const label = session.quote_id ? `Quote #${session.quote_id}` : 'this untitled draft';
            if (! confirm(`Discard ${label}? It will disappear from the resume list. The conversation history is preserved on the quote's history tab.`)) return;
            api.aiAbandonSession(session.id).then(() => {
                this.$emit('discarded', session);
            });
        },

        relativeTime(iso) {
            if (!iso) return 'just now';
            const then = new Date(iso).getTime();
            const now = Date.now();
            const diffSec = Math.max(1, Math.round((now - then) / 1000));
            if (diffSec < 60) return diffSec + 's ago';
            const diffMin = Math.round(diffSec / 60);
            if (diffMin < 60) return diffMin + 'm ago';
            const diffHr = Math.round(diffMin / 60);
            if (diffHr < 24) return diffHr + 'h ago';
            const diffDay = Math.round(diffHr / 24);
            if (diffDay < 7) return diffDay + 'd ago';
            return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        },

        formatTokens(s) {
            const total = (s.input_tokens || 0) + (s.output_tokens || 0);
            if (!total) return '';
            return Math.round(total / 1000) + 'k tokens';
        },
    },
};
</script>

<style>
.ai-draft-resume h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 4px;
}

.ai-draft-resume-list {
    list-style: none;
    margin: 12px 0 0;
    padding: 0;
}

.ai-draft-resume-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid var(--ai-border);
    border-radius: var(--ai-radius-sm);
    background: #fff;
    cursor: pointer;
    transition: border-color .12s, background-color .12s, transform .05s;
}

.ai-draft-resume-item + .ai-draft-resume-item {
    margin-top: 8px;
}

.ai-draft-resume-item:hover {
    background: var(--ai-user-tint);
    border-color: var(--ai-primary);
}

.ai-draft-resume-item:active {
    transform: translateY(1px);
}

.ai-draft-resume-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--ai-user-tint);
    color: var(--ai-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 14px;
}

.ai-draft-resume-body {
    flex: 1;
    min-width: 0;
}

.ai-draft-resume-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--ai-text);
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
}

.ai-draft-resume-tokens {
    font-size: 12px;
    color: var(--ai-muted);
    font-weight: 400;
}

.ai-draft-resume-meta {
    font-size: 12px;
    color: var(--ai-muted);
    margin-top: 2px;
}

.ai-draft-resume-arrow {
    color: var(--ai-muted);
    flex-shrink: 0;
}

.ai-draft-resume-discard {
    background: transparent;
    border: 1px solid transparent;
    color: var(--ai-muted);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color .12s, color .12s;
    opacity: 0;
    font-size: 12px;
}

.ai-draft-resume-item:hover .ai-draft-resume-discard {
    opacity: 1;
}

.ai-draft-resume-discard:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
}
</style>
